/* Shoplift Cart Drawer — Native Popover API controller
   Handles: quick-add intercept, optimistic UI, quantity stepper with View Transitions,
   focus management, live region announcements, error rollback. */
(function() {
  var drawer = document.getElementById('cart-drawer');
  if (!drawer) return;

  var liveRegion = drawer.querySelector('[data-cart-live]');
  var body = drawer.querySelector('[data-cart-body]');
  var lastFocused = null;

  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function announce(msg) {
    if (liveRegion) liveRegion.textContent = msg;
  }

  function startTransition(fn) {
    if (document.startViewTransition && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return document.startViewTransition(fn);
    }
    fn();
    return { finished: Promise.resolve() };
  }

  /* ---- Open / close lifecycle ---- */
  function openDrawer() {
    lastFocused = document.activeElement;
    if (!drawer.matches(':popover-open')) {
      drawer.showPopover();
    }
    document.querySelector('main')?.setAttribute('inert', '');
    // Move focus to close button
    setTimeout(function() {
      drawer.querySelector('.sl-cart__close')?.focus();
    }, 50);
  }

  function closeDrawer() {
    if (drawer.matches(':popover-open')) {
      drawer.hidePopover();
    }
    document.querySelector('main')?.removeAttribute('inert');
    // Restore focus
    if (lastFocused && lastFocused.focus) {
      lastFocused.focus();
    }
  }

  // Listen for popover state changes (handles ESC key automatically)
  drawer.addEventListener('toggle', function(e) {
    if (e.newState === 'open') {
      lastFocused = lastFocused || document.activeElement;
      document.querySelector('main')?.setAttribute('inert', '');
      setTimeout(function() {
        drawer.querySelector('.sl-cart__close')?.focus();
      }, 50);
    } else {
      document.querySelector('main')?.removeAttribute('inert');
    }
  });

  /* ---- Cart state refresh ---- */
  function refreshCart() {
    return fetch('/cart.js')
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        // Update cart count badges everywhere
        document.querySelectorAll('[data-cart-count], .sl-sticky-bar-count, .sl-cart-badge').forEach(function(el) {
          el.textContent = cart.item_count;
          el.style.display = cart.item_count > 0 ? 'flex' : 'none';
        });
        // Inject badge on the header cart icon + set aria-label
        var headerCarts = document.querySelectorAll('header a[href*="/cart"], header button[popovertarget="cart-drawer"], header [aria-label*="cart" i]');
        headerCarts.forEach(function(el) {
          var badge = el.querySelector('.sl-cart-badge');
          if (!badge) {
            badge = document.createElement('span');
            badge.className = 'sl-cart-badge';
            badge.setAttribute('aria-hidden', 'true');
            el.style.position = 'relative';
            el.appendChild(badge);
          }
          if (cart.item_count > 0) {
            badge.textContent = cart.item_count;
            badge.style.display = 'flex';
          } else {
            badge.style.display = 'none';
          }
          // Live aria-label
          el.setAttribute('aria-label', 'Cart, ' + cart.item_count + ' item' + (cart.item_count === 1 ? '' : 's'));
        });
        // Update subtotal
        var subtotal = drawer.querySelector('[data-cart-subtotal]');
        if (subtotal) subtotal.textContent = money(cart.total_price);
        return cart;
      });
  }
  // Initial badge sync
  refreshCart();

  /* ---- Rerender drawer body from cart state ---- */
  function rerenderDrawer() {
    return fetch('/?sections=cart-drawer')
      .then(function(r) { return r.json(); })
      .catch(function() {
        // Fallback: fetch cart.js and rebuild drawer body manually
        return fetch('/cart.js').then(function(r) { return r.json(); }).then(function(cart) {
          rebuildDrawerBody(cart);
        });
      });
  }

  function rebuildDrawerBody(cart) {
    if (!body) return;
    if (cart.item_count === 0) {
      body.innerHTML = '<div class="sl-cart__empty">'
        + '<h3 class="sl-cart__empty-title">Your cart is empty.</h3>'
        + '<p class="sl-cart__empty-sub">The good stuff is waiting.</p>'
        + '<a href="/collections/all" class="sl-cart__empty-cta">Shop All</a>'
        + '</div>';
      var footer = drawer.querySelector('[data-cart-footer]');
      if (footer) footer.remove();
      return;
    }
    var html = '<ul class="sl-cart__items" data-cart-items>';
    cart.items.forEach(function(item, i) {
      html += '<li class="sl-cart__item" data-line="' + (i + 1) + '" data-key="' + item.key + '">';
      html += '<div class="sl-cart__item-img">';
      if (item.image) {
        html += '<img src="' + item.image + '" alt="' + (item.vendor || '') + ' ' + item.title + '" width="80" height="80" loading="lazy">';
      }
      html += '</div><div class="sl-cart__item-info">';
      html += '<span class="sl-cart__item-vendor">' + (item.vendor || '') + '</span>';
      html += '<span class="sl-cart__item-title">' + item.product_title + '</span>';
      if (item.variant_title && item.variant_title !== 'Default Title') {
        html += '<span class="sl-cart__item-variant">' + item.variant_title + '</span>';
      }
      html += '<div class="sl-cart__qty">';
      html += '<button type="button" class="sl-cart__qty-btn" data-qty-action="decrease" aria-label="Decrease quantity">&minus;</button>';
      html += '<span class="sl-cart__qty-value" data-qty-value>' + item.quantity + '</span>';
      html += '<button type="button" class="sl-cart__qty-btn" data-qty-action="increase" aria-label="Increase quantity">&plus;</button>';
      html += '</div>';
      html += '<button type="button" class="sl-cart__remove" data-remove-line>Remove</button>';
      html += '</div>';
      html += '<div class="sl-cart__item-price" data-line-price>' + money(item.final_line_price) + '</div>';
      html += '</li>';
    });
    html += '</ul>';
    body.innerHTML = html;
    // Ensure footer exists
    if (!drawer.querySelector('[data-cart-footer]')) {
      var footer = document.createElement('footer');
      footer.className = 'sl-cart__footer';
      footer.setAttribute('data-cart-footer', '');
      footer.innerHTML = '<div class="sl-cart__subtotal">'
        + '<span class="sl-cart__subtotal-label">Subtotal</span>'
        + '<span class="sl-cart__subtotal-price" data-cart-subtotal>' + money(cart.total_price) + '</span>'
        + '</div>'
        + '<a href="/checkout" class="sl-cart__checkout">Checkout</a>'
        + '<p class="sl-cart__note">Delivery calculated at checkout.</p>';
      drawer.querySelector('[data-cart-live]')?.before(footer);
    } else {
      var sub = drawer.querySelector('[data-cart-subtotal]');
      if (sub) sub.textContent = money(cart.total_price);
    }
  }

  /* ---- Optimistic skeleton ---- */
  function addSkeletonLine() {
    var list = body.querySelector('[data-cart-items]');
    if (!list) {
      // Remove empty state
      var empty = body.querySelector('[data-cart-empty], .sl-cart__empty');
      if (empty) empty.remove();
      list = document.createElement('ul');
      list.className = 'sl-cart__items';
      list.setAttribute('data-cart-items', '');
      body.appendChild(list);
    }
    var skeleton = document.createElement('li');
    skeleton.className = 'sl-cart__item sl-cart__item--skeleton';
    skeleton.setAttribute('data-skeleton', '');
    skeleton.innerHTML = '<div class="sl-cart__item-img sl-skeleton"></div>'
      + '<div class="sl-cart__item-info">'
      + '<span class="sl-skeleton sl-skeleton--text"></span>'
      + '<span class="sl-skeleton sl-skeleton--text sl-skeleton--short"></span>'
      + '</div>'
      + '<div class="sl-cart__item-price sl-skeleton sl-skeleton--price"></div>';
    list.appendChild(skeleton);
    return skeleton;
  }

  /* ---- Error toast ---- */
  function showError(msg) {
    var toast = document.createElement('div');
    toast.className = 'sl-cart__error';
    toast.setAttribute('role', 'alert');
    toast.textContent = msg || 'Something went wrong. Try again.';
    drawer.appendChild(toast);
    setTimeout(function() { toast.remove(); }, 3500);
  }

  /* ---- Add to cart (intercept quick-add forms) ---- */
  document.addEventListener('submit', function(e) {
    var form = e.target;
    if (!form.classList || !form.classList.contains('sl-quick-add')) return;
    e.preventDefault();

    var variantId = form.querySelector('[name="id"]').value;
    var productLink = form.closest('.sl-product-card')?.querySelector('a');
    var productTitle = productLink?.querySelector('.sl-product-title')?.textContent || 'Product';

    // Optimistic: open drawer, show skeleton, update count
    openDrawer();
    var skeleton = addSkeletonLine();

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
    .then(function(r) {
      if (!r.ok) throw new Error('Add failed');
      return r.json();
    })
    .then(function() {
      return refreshCart();
    })
    .then(function(cart) {
      if (skeleton) skeleton.remove();
      rebuildDrawerBody(cart);
      announce('Added ' + productTitle + ' to cart');
    })
    .catch(function() {
      if (skeleton) skeleton.remove();
      showError('Could not add to cart.');
    });
  });

  /* ---- Quantity stepper (View Transitions) ---- */
  drawer.addEventListener('click', function(e) {
    var qtyBtn = e.target.closest('[data-qty-action]');
    var removeBtn = e.target.closest('[data-remove-line]');
    var line = e.target.closest('.sl-cart__item');
    if (!line) return;
    var key = line.getAttribute('data-key');

    if (qtyBtn) {
      var action = qtyBtn.getAttribute('data-qty-action');
      var current = parseInt(line.querySelector('[data-qty-value]').textContent, 10) || 1;
      var next = action === 'increase' ? current + 1 : Math.max(0, current - 1);

      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: next })
      })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        startTransition(function() {
          rebuildDrawerBody(cart);
          refreshCart();
        });
      });
    }

    if (removeBtn) {
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        startTransition(function() {
          rebuildDrawerBody(cart);
          refreshCart();
        });
        announce('Removed from cart');
      });
    }
  });

  /* ---- Expose open method for sticky bar / header cart icon ---- */
  window.shopliftCart = {
    open: openDrawer,
    close: closeDrawer,
    refresh: refreshCart
  };

  /* ---- Intercept any header cart icon click ---- */
  document.addEventListener('click', function(e) {
    var cartLink = e.target.closest('a[href="/cart"], a[href*="/cart"]');
    if (cartLink && !cartLink.hasAttribute('data-bypass-drawer')) {
      // Only intercept in-page cart links, not during checkout
      if (cartLink.getAttribute('href') === '/cart') {
        e.preventDefault();
        openDrawer();
      }
    }
  });
})();
